import React from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {green} from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  TextField,
  FormControl,
  Avatar,
  Button,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputLabel,
  FormLabel,
  FormGroup,
  Select,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { useIdleTimer } from 'react-idle-timer';
import Autocomplete from '@mui/material/Autocomplete';

import { CutCopyPasteTextField, CutCopyPasteNumberField } from './CustomInputs';

import {Dialog as CustomDialog} from './Dialog';

import {FormatPhoneNumber,handleInitials} from '../../Utilities/functions'

import { useParams } from "react-router-dom"

import {CodesetsContext} from '../../StateObjects/CodesetsContext';

import {Auth} from '../../Api/auth';
import { CustomEvents,UserRole,RequestStatusId,Language } from '../../ApplicationConfig/constants';

import {UserInfoContext} from '../../StateObjects/UserInfoContext';

import { useMsal } from "@azure/msal-react";

const PREFIX = 'DialogBox';

const classes = {
  root: `${PREFIX}-root`,
  avatar: `${PREFIX}-avatar`,
  vettingContainerTitle: `${PREFIX}-vettingContainerTitle`,
  vettingSection: `${PREFIX}-vettingSection`,
  vettingRow: `${PREFIX}-vettingRow`,
  vettingColumn: `${PREFIX}-vettingColumn`,
  vettingText: `${PREFIX}-vettingText`,
  footerBtns: `${PREFIX}-footerBtns`,
  textField: `${PREFIX}-textField`,
  widthAuto: `${PREFIX}-widthAuto`,
  alignStart: `${PREFIX}-alignStart`,
  divider: `${PREFIX}-divider`,
  autocompleteOption: `${PREFIX}-autocompleteOption`
};

const StyledDialog = styled(Dialog)(({theme}) => ({
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiFormControlLabel-root': {
    'marginLeft': '0px',
    'flexBasis': '50%',
    'height': '100%',
    '&:last-child': {
      'marginRight': '0px',
    },
    '& .MuiTextField-root': {
      width: '100% !important',
    },
  },
  '& .Mui-error ~.MuiFormHelperText-root, & .Mui-error + label': {
    color: theme.palette.error.main,
  },
  '& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)': {
    height: '100%',
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(1.5, 3),
  },
  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
  },
  '& .MuiDialog-paperWidthSm .MuiOutlinedInput-inputMultiline': {
    maxHeight: 'none',
  },

  [`& .${classes.avatar}`]: {
    backgroundColor: green[500],
    color: theme.palette.grey[100],
  },

  [`& .${classes.vettingContainerTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [`& .${classes.vettingSection}`]: {
    padding: theme.spacing(3),
  },

  [`& .${classes.vettingRow}`]: {
    'display': 'flex',
    'padding': theme.spacing(1.5, 0),
    'flexFlow': 'row',
    'height': 'auto',
    'justifyContent': 'center',
    'width': '100%',
    'alignItems': 'center',
    '&:first-of-type': {
      paddingTop: 0,
    },
    '&:last-child': {
      paddingBottom: 0,
    },
  },

  [`& .${classes.vettingColumn}`]: {
    'display': 'flex',
    'flexDirection': 'column',
    'width': '100%',
    'justifyContent': 'center',
    'marginRight': theme.spacing(1),
    'height': '100%',
    '&:last-child': {
      marginRight: 0,
    },
  },

  [`& .${classes.vettingText}`]: {
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.footerBtns}`]: {
    marginLeft: theme.spacing(2),
  },

  [`& .${classes.textField}`]: {
    width: '100%',
    padding: 0,
  },

  [`& .${classes.widthAuto}`]: {
    width: 'auto !important',
  },

  [`& .${classes.alignStart}`]: {
    alignItems: 'start',
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },

  [`& .${classes.autocompleteOption}`]: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
  }
}));

export function DialogAnalyst(props) {

  const {t} = useTranslation();
  
  const {open, toggleDialog,account,header} = props;

  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper">
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {header}
          </Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
      <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={clsx(classes.vettingColumn, classes.widthAuto)}>
              <Avatar className={classes.avatar}>{account.account.substring(0,1).toUpperCase()}</Avatar>
            </div>
          <div className={classes.vettingColumn}>
           
              <Typography className={classes.dialogText} variant="body2">
              {account.fullName}
              </Typography>
              <Typography className={classes.dialogText} variant="body2">
              {account.email}
              </Typography>
             {account.phoneNumber && <Typography className={classes.dialogText} variant="body2">
              +{FormatPhoneNumber(account.phoneNumber)}
              </Typography>}
          </div>
        </div>
      </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" color="primary" onClick={toggleDialog}>
        {t('dialog.close')}
        </Button>
       </DialogActions>
    </StyledDialog>
  );
}
DialogAnalyst.propTypes={
  open: PropTypes.bool.isRequired, 
  toggleDialog: PropTypes.func.isRequired,
  account: PropTypes.object,
  header: PropTypes.string.isRequired,
}

export function DialogWithdraw(props) {
  const {t} = useTranslation();
  let { lang } = useParams();
  const {codesets} = React.useContext(CodesetsContext);

  const {
    toggleDialog, 
    handleWithdraw,
    rowData,
    open,
    snackbar,
  } = props;

  const initial = {
    // blank object used to reset state
    withdrawReason: {
      text: '',
      errorText: '',
    },
  };
  
  const [state, setState] = React.useState({...initial});

  React.useEffect(()=>{
    setState({...initial})
  },[open])

  const handleSelectChange = (event) => {
    const name = event.target.name;
    setState({
      ...state,
      [name]: {
        text: event.target.value,
        errorText: '',
      },
    });
  };

  const validateForm = () => {
    let isError = false;
    
    if (typeof state.withdrawReason.text=='string') {
      isError = true;
      state.withdrawReason.errorText = t('dialog.withdrawReason');
    }

    if (isError) {
      setState({
        ...state,
      });
    }

    return isError;
  };

  const submitForm = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const err = validateForm();
    if (!err) { // if no errors exist, submit the form and reset the inputs
      toggleDialog(e);
      handleWithdraw({...rowData,reason_id:state.withdrawReason.text})
      setState({...initial});
    } else {
      for (const property in state) { // focus on the first input that has an error on submit
        if (state[property].errorText) {
          if (property=='withdrawReason'){
            document.getElementById('withdrawReason').focus();
          }
          break;
        }
      }
    }
  };

  const addOtherMenuItem=(codeset,lang)=>{
    let otherItemResult=codeset.filter(item=>item.id==6)
    
    if (otherItemResult.length >0){
      return (
      <MenuItem 
        key={otherItemResult[0].id} 
        value={otherItemResult[0].id}
      >
        {lang==Language.English?otherItemResult[0].label_en:otherItemResult[0].label_fr}
      </MenuItem>
      );
    }
    else{
      return <Root></Root>;
    }
  }

  const content = () => (
    <>
      <FormControl
        sx={{m:0}}
        variant="outlined"
        error={Boolean(state.withdrawReason.errorText)}
        margin="dense"
        required
        fullWidth
      >
        <InputLabel id="withdrawReason-label">
          {t('dialog.withdrawReasonLabel')}
          <span className="screen-reader-text">{t('inputs.required')}</span>
        </InputLabel>
        <Select
          id="withdrawReason"
          name="withdrawReason"
          label={t('dialog.withdrawReasonLabel') + ' *'}
          labelId="withdrawReason-label"
          onChange={handleSelectChange}
          value={state.withdrawReason.text}
        >
          {codesets.withdrawReasons.filter(item=>item.id!=6)
          .sort(function(a,b){
            if (lang==Language.English){
              a=a.label_en.toLowerCase()
              b=b.label_en.toLowerCase()
            }
            else{
              a=a.label_fr.toLowerCase()
              b=b.label_fr.toLowerCase()
            }

            return a < b ? -1 : a > b ? 1 : 0;
          })
          .map((item)=>{
            let label=lang==Language.English?item.label_en:item.label_fr;
            return <MenuItem key={item.id} value={item.id}>{label}</MenuItem>
          })}
          {addOtherMenuItem(codesets.withdrawReasons,lang)}
        </Select>
      </FormControl>
      {Boolean(state.withdrawReason.errorText) && (
        <FormHelperText
          error={Boolean(state.withdrawReason.errorText)}
          variant="outlined"
          margin="dense"
        >
          {state.withdrawReason.errorText}
        </FormHelperText>
      )}
      {snackbar}
    </>
  );

  return (
    <>
      <CustomDialog
        id="withdraw-dialog"
        open={props.open}
        title={t('dialog.withdrawTitle')}
        content={content()}
        primaryButton={t('dialog.withdrawRequest')}
        secondaryButton={t('dialog.withdrawCancel')}
        handlePrimaryClick={submitForm}
        handleSecondaryClick={(e) => props.toggleDialog(e)}
        toggleDialog={(e) => props.toggleDialog(e)}
      />
      {snackbar}
    </>
  );
}
DialogWithdraw.propTypes={
  toggleDialog: PropTypes.func.isRequired, 
  handleWithdraw: PropTypes.func.isRequired,
  rowData: PropTypes.object,
  open: PropTypes.bool,
  snackbar: PropTypes.element,
}

//////////////////////////////////////////// SAVE BEFORE LEAVING
export function DialogSaveBeforeLeaving({toggleDialog, open, handleDontSave, handleSave}) {

  const {t} = useTranslation();

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      aria-describedby="dashboard-dialog-description"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t("dialog.saveBeforeLeaveTitle")}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Alert
                id="dashboard-dialog-description"
                severity="warning"
                className={classes.alert}
                role="note"
              >
                {t("dialog.saveBeforeLeaveMessage")}
              </Alert>
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Button
              id="saveBeforeLeave-noSave-btn"
              color="primary"
              onClick={handleDontSave}
              className={clsx(classes.footerBtn, 'MuiIconButton-edgeStart')}
              onKeyPress={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.key === 'Enter') {
                  handleDontSave();
                }
              }}
            >
              {t("dialog.saveBeforeLeaveDontSave")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={toggleDialog}
              className={classes.footerBtns}
              onKeyPress={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.key === 'Enter') {
                  toggleDialog(e);
                }
              }}
            >
              {t("dialog.saveBeforeLeaveCancel")}
            </Button>
            <Button
              autoFocus
              id="saveBeforeLeave-save-btn"
              variant="contained"
              color="primary"
              onClick={handleSave}
              className={classes.footerBtns}
              onKeyPress={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if(e.key === 'Enter'){
                  handleSave();
                }
              }}
            >
              {t("dialog.saveBeforeLeaveSave")}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </StyledDialog>
  );
}
DialogSaveBeforeLeaving.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleDontSave: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
}

export function DialogUpdate(props) {

  const {toggleDialog, open, handleUpdate,rowData} = props;
  const {t} = useTranslation();

  const submitForm = () => {
    handleUpdate({...rowData});
  };

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={(e) => {
        toggleDialog(e);
      }}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6' component="h2">
            {t('dialog.updateTitle')}
          </Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge='end'
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Typography variant="subtitle1" component="h3">
                {t('dialog.updateContentPara1')}
              </Typography>
            </div>
          </div>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <ul>
                <li>
                  <Typography variant="body2" gutterBottom>
                    {t('dialog.updateContentPara2')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {t('dialog.updateContentPara3')}
                  </Typography>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) => {
            toggleDialog(e);
          }}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          className={classes.footerBtns}
        >
          {t('dialog.updateCancel')}
        </Button>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.footerBtns}
          onClick={(e) => {
            toggleDialog(e);
            submitForm();
          }}
          onKeyPress={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
              submitForm();
            }
          }}
        >
          {t('dialog.updateSubmitRequest')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogUpdate.propTypes={
  toggleDialog: PropTypes.func.isRequired, 
  open: PropTypes.bool.isRequired, 
  handleUpdate: PropTypes.func.isRequired,
  rowData: PropTypes.object,
  snackbar: PropTypes.element,
}

export function DialogApproved(props) {

  const {t} = useTranslation();
  const {toggleDialog, open,rowData,handleApproved} = props;

  const initial = {
    hours: {
      text: '',
      errorText: '',
    },
    minutes: {
      text: '',
      errorText: '',
    },
  };

  const [state, setState] = React.useState({
    ...initial
  });

  const validateForm = () => {
    let isError = false;
    if (state.hours.text.trim() === '') {
      isError = true;
      state.hours.errorText = t('dialog.approveBillabeHoursError');
    }
    if (state.minutes.text.trim() === '') {
      isError = true;
      state.minutes.errorText = t('dialog.approveBillabeMinutesError');
    }

    if (isError) {
      setState({
        ...state,
      });
    }

    return isError;
  };

  const handleChange = (e, val) => {
    const comment = e.target.value;
    setState({...state, [val]: {
      ...state[val],
      text: comment,
    },
    });
    if (e.target.value && state[val].errorText) { // if input text is valid, clear error
      setState({...state, [val]: {
        ...state[val],
        text: comment,
        errorText: '',
      },
      });
    }
  };

  const handleSubmit=(e)=>{
    e.stopPropagation();
    e.preventDefault();
    const err = validateForm();
    if (!err) {
      toggleDialog(e)
      handleApproved({
        ...rowData,
        billableHours: state.hours.text.length == 0 ? '0' : state.hours.text,
        billableMinutes: state.minutes.text.length == 0 ? '0' : state.minutes.text
      })
      setState({...initial});
    } else {
      for (const property in state) { 
        // focus on the first input that has an error on submit
        if (state[property].errorText) {
          switch (property) {
            case 'hours':
              document.getElementById('hours-input').focus();
              break;
            case 'minutes':
              document.getElementById('minutes-input').focus();
              break;
            default:
              break;
          }
          break;
        }
      }
    }  
  }

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6' component="h2">
            {t('dialog.approvedTitle')}
          </Typography>
          <IconButton
            id='dialog-close'
            onClick={(e) => {
              setState({...initial});
              toggleDialog(e);
            }}
            edge='end'
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate id="approve-form">
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant='subtitle2' component="h3">
                  {t('dialog.approvedBillableHours')}
                </Typography>
              </div>
            </div>
            <div className={clsx(classes.vettingRow, classes.alignStart)}>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined">
                      <CutCopyPasteNumberField
                        id="hours-input"
                        label={t('dialog.approvedLabelHours')}
                        value={state.hours.text}
                        customInput={TextField}
                        type="text"
                        variant='outlined'
                        error={Boolean(state.hours.errorText)}
                        helperText={state.hours.errorText}
                        required
                        decimalScale={0}
                        allowNegative={false}
                        onChange={(e)=> handleChange(e,'hours')}
                      />
                </FormControl>
              </div>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined">
                      <CutCopyPasteNumberField
                        id="minutes-input"
                        label={t('dialog.approvedLabelMinutes')}
                        value={state.minutes.text}
                        customInput={TextField}
                        type="text"
                        variant='outlined'
                        isAllowed={(values) => {
                          const {formattedValue, floatValue} = values;
                          return formattedValue === '' || floatValue <= 59;
                        }}
                        decimalScale={0}
                        allowNegative={false}
                        error={Boolean(state.minutes.errorText)}
                        helperText={state.minutes.errorText}
                        required
                        onChange={(e) => handleChange(e, 'minutes')}
                      />
                </FormControl>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
      <Divider />

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={(e) => {
            setState({...initial});
            toggleDialog(e);
          }} className={classes.footerBtns}
        >
          {t('dialog.approvedCancel')}
        </Button>
        <Button  type="submit" variant="contained" color="primary" className={classes.footerBtns} form="approve-form">
          {t('dialog.approvedSubmit')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogApproved.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool,
  rowData: PropTypes.object,
  handleApproved: PropTypes.func.isRequired,
  snackbar: PropTypes.element,
}

export function DialogDelete(props) {

  const {t} = useTranslation();
  const {toggleDialog, handleDelete, open, rowData} = props;

  const handleSubmit=(e)=>{
    toggleDialog(e)
    handleDelete({...rowData})
  }

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      aria-describedby="dashboard-dialog-description"
      open={open}
      className={classes.root}
      onClick={handleClick}
      scroll="paper"
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6' component="h2">
            {t('dialog.deleteTitle')}
          </Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge='end'
            arial-label={t('dialog.deleteLabelClose')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Alert
                id="dashboard-dialog-description"
                severity="warning"
                className={classes.alert}
                role="note"
              >
                {t('dialog.deleteText')}
              </Alert>
            </div>
          </div>
        </div>
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button 
          variant="outlined" 
          color="primary"             
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          onClick={toggleDialog} 
          className={classes.footerBtns}
        >
          {t('dialog.deleteCancel')}
        </Button>
        <Button 
          autoFocus
          id="confirm-delete-btn" 
          variant="contained" 
          color="primary" 
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              handleSubmit(e);
            }
          }} 
          onClick={handleSubmit} 
          className={classes.footerBtns}
        >
          {t('dialog.deleteSubmit')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogDelete.propTypes={
  toggleDialog: PropTypes.func.isRequired, 
  handleDelete: PropTypes.func,
  open: PropTypes.bool.isRequired,
  rowData: PropTypes.object,
  snackbar: PropTypes.element,
}

export function DialogDenied(props) {


  const {t} = useTranslation();

  const {codesets} = React.useContext(CodesetsContext);
  let { lang } = useParams();
  const {toggleDialog, handleDenied,rowData,open} = props;
  
  const initial = {
    hours: {
      text: '',
      errorText: '',
    },
    minutes: {
      text: '',
      errorText: '',
    },
    reason: {
      text: '',
      errorText: '',
    }
  };

  const [state, setState] = React.useState({...initial});

  const handleChange = (e, val) => {
    const comment = e.target.value;
    setState({...state, [val]: {
      ...state[val],
      text: comment,
    },
    });
    if (e.target.value && state[val].errorText) { // if input text is valid, clear errors
      setState({...state, [val]: {
        ...state[val],
        text: comment,
        errorText: '',
      },
      });
    }
  };

  const validateForm = () => {
    let isError = false;

    state.minutes.errorText=''
    state.hours.errorText=''
    state.reason.errorText=''
    
    setState({...state})
    if (state.hours.text.trim() === '') {
      isError = true;
      state.hours.errorText =  t('dialog.deniedBillabeHoursError');
    }
    if (state.minutes.text.trim() === '') {
      isError = true;
      state.minutes.errorText =  t('dialog.deniedBillabeMinutesError');
    }
    if (state.reason.text.toString().trim() === '') {
      isError = true;
      state.reason.errorText = t('dialog.deniedReasonError');
    }

    if (isError) {
      setState({
        ...state,
      });
    }

    return isError;
  };

  const submitForm=(e)=>{
    e.stopPropagation();
    e.preventDefault();
    const err = validateForm();
    if (!err) { // if no errors exist, submit the form, toggle snackbar, and reset the inputs

      toggleDialog(e);
      //get the content of withdraw comment box
      handleDenied({...rowData,
        reasonId:state.reason.text,
        billableHours:state.hours.text,
        billableMinutes:state.minutes.text
      })

      setState({...initial});

    } else {
      for (const property in state) { 
        // focus on the first input that has an error on submit
        if (state[property].errorText) {
          switch (property) {
            case 'hours':
              document.getElementById('hours-input').focus();
              break;
            case 'minutes':
              document.getElementById('minutes-input').focus();
              break;
            case 'reason':
              document.getElementById('denied-select-label').focus();
              break;
            default:
              break;
          }
          break;
        }
      }
    }
  }

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={(e) => {
        setState({...initial});
        toggleDialog(e);
      }}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6' component="h2">
            {t('dialog.deniedTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge='end'
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={submitForm} noValidate id="deny-form">
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant="subtitle2" component="h3">
                  {t('dialog.deniedBillableHours')}
                </Typography>
              </div>
            </div>
    
            <div className={clsx(classes.vettingRow, classes.alignStart)}>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined">
                      <CutCopyPasteNumberField
                        id="hours-input"
                        required
                        label={t('dialog.deniedLabelHours')}
                        value={state.hours.text}
                        customInput={TextField}
                        type="text"
                        variant='outlined'
                        decimalScale={0}
                        allowNegative={false}
                        error={Boolean(state.hours.errorText)}
                        helperText={state.hours.errorText}
                        onChange={(e) => handleChange(e, 'hours')}
                      />
                </FormControl>
              </div>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined">
                        <CutCopyPasteNumberField
                          id="minutes-input"
                          label={t('dialog.deniedLabelMinutes')}
                          customInput={TextField}
                          type="text"
                          variant='outlined'
                          isAllowed={(values) => {
                            const {formattedValue, floatValue} = values;
                            return formattedValue === '' || floatValue <= 59;
                          }}
                          decimalScale={0}
                          allowNegative={false}
                          error={Boolean(state.minutes.errorText)}
                          helperText={state.minutes.errorText}
                          required
                          onChange={(e) => handleChange(e, 'minutes')}
                        />
                </FormControl>
              </div>
            </div>

            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <FormControl
                  sx={{m:0}}
                  margin="dense"
                  required
                  variant="outlined"
                  fullWidth
                >
                  <InputLabel id="denied-select-label">
                    {t('dialog.deniedReason')}
                    <span className="screen-reader-text">{t('inputs.required')}</span>
                  </InputLabel>
                  <Select
                    id="denied-select"
                    labelId="denied-select-label"
                    value={state.reason.text}
                    label={t('dialog.deniedReason') + ' *'}
                    placeholder={t('dialog.deniedSelectOption')}
                    onChange={(e) => handleChange(e, 'reason')}
                    error={Boolean(state.reason.errorText)}
                  >
                    {codesets.deniedReasons.map((item)=>{
                      let label=lang==Language.English?item.label_en:item.label_fr;
                      return <MenuItem key={item.id} value={item.id}>{label}</MenuItem>
                    })}
                  </Select>
                  <FormHelperText>{state.reason.errorText}</FormHelperText>
                </FormControl>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
      <Divider  />
      <DialogActions>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={(e) => {
            setState({...initial});
            toggleDialog(e);
          }} 
          className={classes.footerBtns}
        >
          {t('dialog.deniedCancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary" className={classes.footerBtns} form="deny-form">
          {t('dialog.deniedSubmit')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogDenied.propTypes={
  toggleDialog: PropTypes.func.isRequired, 
  handleDenied: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  rowData: PropTypes.object,
}

//////////////////////////////////////////// GET SUPPORT (FORM ONLY)
export function DialogFormGetSupportFab(props) {

  const {t} = useTranslation();
  const {toggleDialog, open,handleSupportSubmit} = props;
  const {codesets} = React.useContext(CodesetsContext);
  let { lang } = useParams();
  let steps=[]
  const [state, setState] = React.useState({steps})

  React.useEffect(()=>{
    let steps=[]
    codesets.supportForm.forEach(function(item){
      steps.push({'id':'step'+ item.id,'checked':false})
    })
    setState({steps})
  },[open])

  const handleChbxChange = (event) => {
    state.steps.filter(item=>item['id']==event.target.name)[0]['checked']=true
    setState(state)
  };

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={(e) => {
        toggleDialog(e);
      }}
      aria-labelledby="get-support-form-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="get-support-form-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t('dialog.supportFormTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
            {t('dialog.supportFormContentPara1')}
          </Typography>
          <FormControl component="fieldset" sx={{mb: 2}} required aria-describedby="support-helperText">
            <FormLabel component="legend">
              {t('dialog.supportFormContentPara2')}
              <span className="screen-reader-text">{t('inputs.required')}</span>
            </FormLabel>
            <FormHelperText id="support-helperText">{t('dialog.supportFormContentPara3')}</FormHelperText>
            <FormGroup>
            {codesets.supportForm.map((item)=>{
              let label=lang==Language.English?item.label_en:item.label_fr;
              let divider= <></>
              if (item.id==18){
                divider= <Divider sx={{mb: 2}} />
              }
              return (
                <React.Fragment key={item.id}>
                  {divider}
                  <FormControlLabel 
                    key={item.id}
                    control={
                      <Checkbox
                        checked={state.steps.filter(rec=>rec.id=='step' + item.id)['checked']}
                        onChange={handleChbxChange}
                        name={"step" + item.id}
                        color="primary"
                      />
                    }
                    label={label}
                  />
                </React.Fragment> 
              );
            })}
            </FormGroup>
          </FormControl>
          <Typography variant="body2">
          {t('dialog.supportFormContentPara4')}
          </Typography>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) => {
            toggleDialog(e);
          }}
          className={classes.footerBtns}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
        >
          {t('dialog.supportFormCancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.footerBtns}
          onClick={(e) => {
            toggleDialog(e);
              handleSupportSubmit(state.steps)
          }}
          onKeyPress={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
              handleSupportSubmit(state.steps)
            }
          }}
        >
          {t('dialog.supportFormSubmit')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogFormGetSupportFab.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleSupportSubmit: PropTypes.func.isRequired,
  snackbar: PropTypes.element,
}

export function DialogGetSupportFab(props){

  const {t} = useTranslation();
  const {toggleDialog, open,handleSupportSubmit} = props;

  const handleSubmit=(e)=>{
    toggleDialog(e);
    handleSupportSubmit()
  }
  
  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      open={open}
      onClose={(e) => {
        toggleDialog(e);
      }}
      aria-labelledby="alert-dialog-title"
      scroll='paper'
      className={classes.root}
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="alert-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t("dialog.supportTitle")}
          </Typography>
          <IconButton
            edge="end"
            aria-label={t("dialog.supportClose")}
            onClick={toggleDialog}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pb: 0 }}>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Typography variant="subtitle2" component="h3">
                {t("dialog.supportContentPara1")}
              </Typography>
            </div>
          </div>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Typography variant="body2">
                {t("dialog.supportContentPara2")}
              </Typography>
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogFooter}>
        <Button
          onClick={(e) => {
            toggleDialog(e);
          }}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          color="primary"
          variant="outlined"
        >
          {t("dialog.supportCancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          className={classes.footerBtns}
          onKeyPress={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              handleSubmit(e);
            }
          }}
        >
          {t("dialog.supportSubmit")}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogGetSupportFab.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleSupportSubmit: PropTypes.func.isRequired,
  snackbar: PropTypes.element,
}

export function DialogNewRequestTitle(props){

  const {t} = useTranslation();
  const {toggleDialog, open,handleRequestSubmission, foldersList} = props;
  const initial = {name: t('dialog.newRequestDefaultTitle'), folder: "", folderError: ""}; // blank object used to reset state
  const [state, setState] = React.useState({...initial});
  
  React.useEffect(()=>{
    if(open){
      setState({...initial});
    }
  },[open])

  const handleChange = (e) => {
    let error = state.folderError
    if(e.target.name==="folder"){
      error="";
    }
    setState({...state, [e.target.name]: e.target.value, folderError: error});
  };

  const handleOnblur=(e)=>{
    if(e.target.value==""){
      setState({...state, name: initial.name});
    }
  }

  const submitForm = (e) => {
    e.preventDefault();
    
    if(state.folder===""){
      setState({...state, folderError: t("dialog.newRequestFolderError")});
    }else{
      toggleDialog();
      handleRequestSubmission(state);
    }
  };
  
  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      maxWidth="md">
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6' component="h2">
            {t('dialog.newRequestTitle')}
          </Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge='end'
            aria-label={t('dialog.newRequestClose')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={submitForm} noValidate id="new-form">
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography
                  variant="subtitle2"
                  component="h3"
                  sx={{mb: 3}}
                >
                  {t('dialog.newRequestLabelName')}
                </Typography>
                <Typography component="p" variant="body2">
                  {t('dialog.newRequestLabelDescription')}
                </Typography>
              </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Autocomplete
                  sx={{m:0}}
                  componentsProps={{
                    clearIndicator: {tabIndex: 0},
                  }}
                  classes={{
                    option: classes.autocompleteOption,
                  }}
                  id="requestFolder"
                  options={foldersList}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      multiline
                      minRows="1"
                      label={t('dialog.newRequestLabelFolder')}
                      required={true}
                      variant="outlined"
                      error={Boolean(t(state.folderError))}
                      helperText={t(state.folderError)}
                    />
                  )}
                  value={state.folder==="" ? null : state.folder}
                  onChange={(e, newValue)=>{
                    if(newValue===null){
                      newValue="";
                    }
                    setState({...state, folder: newValue, folderError: ""});
                  }}
                />
              </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined" className={classes.textField}>
                  <CutCopyPasteTextField 
                    id="name-input"
                    name="name"
                    label={t('dialog.newRequestLabelReqName')}
                    aria-label={t('dialog.newRequestLabelReqName')}
                    value={state.name}
                    variant="outlined"
                    onChange={(e) => handleChange(e)}
                    onClick={handleOnblur}
                    onBlur={handleOnblur}
                  />
                </FormControl>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
      <Divider  />
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={toggleDialog}
          className={classes.footerBtns}
        >
          {t('dialog.newRequestCancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.footerBtns}
          form="new-form"
        >
          {t('dialog.newRequestCreate')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
DialogNewRequestTitle.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleRequestSubmission: PropTypes.func.isRequired,
  foldersList: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export function TimeoutDialog(props) {
  const {t} = useTranslation();

  const [openTimeout, setOpenTimeout] = React.useState(false);
  const timeoutRef = React.useRef(null);
  const [counter, setCounter] = React.useState(0);
  const tabToken=document.getElementById("tabToken").innerHTML;
  const {instance}=useMsal();

  const handleLogout = () => {
    clearTimeout(timeoutRef.current);
    setOpenTimeout(false);
   
    Auth.authorizedFetch("/validateLogout/" + tabToken,{},instance)
    .then(res => res.json())
    .then((result) => {
      if (result.statusCode==200){

        instance.logoutRedirect().catch(e => {
          console.error(e);
        });

        Auth.logout();          
       
      }
      else{
        props.setValidWindowTab(false)
      }
    })
    .catch(
      (error)=>{
        console.log(error);
      }
    )
  }

  const handleOnIdle = () => {
    if(!openTimeout){
      timeoutRef.current = setTimeout(handleLogout, 1000*60*5);
      setCounter(60*5);
      setOpenTimeout(true);
    }
  }

  const handleClose = (e) => {
    if((openTimeout && 
    (e.type==="keydown" && e.key==="Escape" ||
    e.type==="click" || e.type==="mousedown")
    ) || !openTimeout){
      Auth.authorizedFetch("/refreshSession/" + tabToken,{},instance)
      .then(res=>res.json())
      .then((result)=>{
        if(result.statusCode==403){
          Auth.logout()
          window.location.href=result.data.redirectUrl
        }
      })
      .catch((error)=>{
        console.log(error);
      })
      
      clearTimeout(timeoutRef.current);
      setOpenTimeout(false);
    }
  }
  
  const idleTimer = useIdleTimer({
    timeout: 1000 * 60 * 15,//ms to seconds to minutes
    events: [
      'keydown',
      'mousedown',
      CustomEvents.PageLoaded
    ],
    debounce: !openTimeout ? 2000 : 0,
    onIdle: handleOnIdle,
    onAction: handleClose
  })

  React.useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]); 

  return (
    <StyledDialog
      open={openTimeout}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={classes.root}
    >
      <DialogTitle id="alert-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t("site.session.sessionTitle")}
          </Typography>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Alert
                id="alert-dialog-description"
                severity="warning"
                className={classes.alert}
                role="note"
              >
                {t("site.session.sessionClosing", {time: new Date(counter * 1000).toISOString().substring(11, 19)})}
              </Alert>
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button autoFocus variant="contained" color="primary" onClick={handleClose}>{t("site.session.extendSession")}</Button>
      </DialogActions>
    </StyledDialog>
  );
}
TimeoutDialog.propTypes={
  setValidWindowTab: PropTypes.func.isRequired
}

//////////////////////////////////////////// Requester INFORMATION
export function DialogAssigneeDetails(props) {
  const {open, toggleDialog,account,statusHead,supportAnalysts} = props;
  const {t} = useTranslation();

  const {userInfo} = React.useContext(UserInfoContext);

  const resolved = () => {
    return (userInfo.role === UserRole.Analyst && (statusHead === RequestStatusId.Approved || statusHead === RequestStatusId.Denied))
  };

  const handleClick = (e) => {
    e.stopPropagation();
  };
  let supportContent=<></>
  
  if (resolved){
    supportContent= supportAnalysts.map((analyst)=>{
      return (   
        <div className={classes.vettingRow} key={analyst.account}>
          <div className={clsx(classes.vettingColumn, classes.widthAuto)}>
            <Avatar className={classes.avatar}>{handleInitials(analyst.firstName,analyst.lastName)}</Avatar>
          </div>
          <div className={classes.vettingColumn}>
            <Typography className={classes.vettingText} variant="body2">
              {analyst.fullName}
            </Typography>
            <Typography className={classes.vettingText} variant="body2">
              {analyst.email}
            </Typography>
            {analyst.phoneNumber && 
              <Typography className={classes.dialogText} variant="body2">
                +{FormatPhoneNumber(analyst.phoneNumber)}
              </Typography>
            }
          </div>
        </div>
      );
    })
  }

  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t('dialog.assigneeDetailsTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {!resolved() && 
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={clsx(classes.vettingColumn, classes.widthAuto)}>
                <Avatar className={classes.avatar}>{handleInitials(account.firstName,account.lastName)}</Avatar>
              </div>
              <div className={classes.vettingColumn}>
                <Typography className={classes.vettingText} variant="body2">
                  {account.fullName}
                </Typography>
                <Typography className={classes.vettingText} variant="body2">
                  {account.email}
                </Typography>
                {account.phoneNumber && 
                  <Typography className={classes.dialogText} variant="body2">
                    +{FormatPhoneNumber(account.phoneNumber)}
                  </Typography>
                }
              </div>
            </div>
          </div>
        }
        {resolved() && 
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant="subtitle1" component="h3">
                  {t('dialog.assigneeDetailsTitle2')}
                </Typography>
              </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant="subtitle2" component="h4">
                  {t('dialog.assigneeDetailsTitleLead')}
                </Typography>
              </div>
            </div>
            {account.account.length > 0 && 
              <div className={classes.vettingRow}>
                <div className={clsx(classes.vettingColumn, classes.widthAuto)}>
                  <Avatar className={classes.avatar}>{handleInitials(account.firstName,account.lastName)}</Avatar>
                </div>
                <div className={classes.vettingColumn}>
                  <Typography className={classes.vettingText} variant="body2">
                    {account.fullName}
                  </Typography>
                  <Typography className={classes.vettingText} variant="body2">
                    {account.email}
                  </Typography>
                  {account.phoneNumber && 
                    <Typography className={classes.dialogText} variant="body2">
                      +{FormatPhoneNumber(account.phoneNumber)}
                    </Typography>
                  }
                </div>
              </div>
            }
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant="subtitle2" component="h4">
                  {t('dialog.assigneeDetailsSupport')}
                </Typography>
              </div>
            </div>
            {supportContent}
          </div>
        }
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" color="primary" onClick={toggleDialog}>
          {t('dialog.close')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
DialogAssigneeDetails.propTypes={
  open: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  account: PropTypes.object.isRequired,
  statusHead: PropTypes.number.isRequired,
  supportAnalysts: PropTypes.arrayOf(PropTypes.object).isRequired,
}

//////////////////////////////////////////// Requester INFORMATION
export function DialogRequesterDetails(props) {
  const {open, toggleDialog, account} = props;
  const {t} = useTranslation();


  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t('dialog.requesterDetailsTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={clsx(classes.vettingColumn, classes.widthAuto)}>
              <Avatar className={classes.avatar}>{handleInitials(account.firstName,account.lastName)}</Avatar>
            </div>
            <div className={classes.vettingColumn}>
              <Typography className={classes.vettingText} variant="body2">
                {account.fullName}
              </Typography>
              <Typography className={classes.vettingText} variant="body2">
                {account.email}
              </Typography>
              {account.phoneNumber && 
                <Typography className={classes.dialogText} variant="body2">
                  +{FormatPhoneNumber(account.phoneNumber)}
                </Typography>
              }
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" color="primary" onClick={toggleDialog}>
          {t('dialog.close')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
DialogRequesterDetails.propTypes={
  open: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  account: PropTypes.object.isRequired,
}

export function DialogNoLead(props) {

  const {t} = useTranslation();
  const {toggleDialog, open, handleSubmit, text} = props;
  
  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      aria-describedby="dashboard-dialog-description"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {text.title}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
              <Alert
                id="dashboard-dialog-description"
                severity="warning"
                className={classes.alert}
                role="note"
              >
                {text.main}
              </Alert>
            </div>
          </div>
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={toggleDialog}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          className={classes.footerBtns}
        >
          {t('dialog.noLeadCancel')}
        </Button>
        <Button
          autoFocus
          id="noLead-confirm-btn"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
          className={classes.footerBtns}
        >
          {text.submit}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogNoLead.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  text: PropTypes.object.isRequired,
  snackbar: PropTypes.element,
}

export function DialogAssign(props){

  const {t} = useTranslation();
  const {toggleDialog, open, handleAssignMeAs, rowData, isAssignLead}=props;
  // blank object used to reset state
  const initial = {
      text: '',
      errorText: ''
  };
  const [state, setState] = React.useState({
    ...initial
  });

  const phoneExp = /^[+]1\s\(\d{3}\)\s\d{3}\s\d{4}/;

  const handleChange = (e) => {
    const comment = e.target.value.trim();

    if (e.target.value.match(phoneExp) && state.errorText) {
      // if input text is valid, clear error
      setState({
        ...state,
        text: comment,
        errorText: ''
      });
    }else{
      setState({
        ...state,
        text: comment
      });
    }
  };

  const validateForm = () => {
    let isError = false;
    if (!state.text.match(phoneExp)) {
      isError = true;
      setState({
        ...state,
        errorText: t('dialog.assignPhoneNumberError')
      });
    }

    return isError;
  };

  const submitForm = (e) => {
    e.preventDefault();
    const err = validateForm();
    if (!err) {
      // if no errors exist, submit the form and reset the inputs
      toggleDialog(e);
      setState({...initial});
      handleAssignMeAs(rowData!=undefined ? {...rowData, phoneNumber:state.text} : state.text, isAssignLead ? 'lead' : 'support');
    } else {
      if(state.errorText){
        document.getElementById('phone-input').focus();
      }
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return <>
    <StyledDialog
      onClose={() => {
        setState({...initial});
        toggleDialog();
      }}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {isAssignLead ? t('dialog.assignAsLeadTitle') : t('dialog.assignAsSupportTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={submitForm} noValidate id="assign-form">
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <Typography variant="body2">
                  {t('dialog.assignAsLeadMainText')}
                </Typography>
              </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <FormControl variant="outlined" className={classes.textField}>
                  <CutCopyPasteNumberField 
                    id="phone-input"
                    label={t('dialog.assignLabelPhoneNumber')}
                    value={state.text}
                    customInput={TextField}
                    type="text"
                    variant="outlined"
                    format="+1 (###) ### ####"
                    mask="_"
                    allowEmptyFormatting
                    allowNegative={false}
                    autoComplete="tel-national"
                    error={Boolean(state.errorText)}
                    helperText={state.errorText}
                    required
                    onChange={(e) => handleChange(e)}
                  />
                </FormControl>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) => {
            setState({...initial});
            toggleDialog(e);
          }}
          className={classes.footerBtns}
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
        >
          {t('dialog.assignAsLeadCancel')}
        </Button>
        <Button
          id="assign-submit"
          type="submit"
          variant="contained"
          color="primary"
          className={classes.footerBtns}
          form="assign-form"
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              submitForm(e);
            }
          }}
        >
          {t('dialog.assignAsLeadApply')}
        </Button>
      </DialogActions>
      {props.snackbar ? props.snackbar : null}
    </StyledDialog>
    {props.snackbar ? props.snackbar : null}
  </>;
}
DialogAssign.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleAssignMeAs: PropTypes.func.isRequired,
  rowData: PropTypes.object,
  isAssignLead: PropTypes.bool.isRequired,
  snackbar: PropTypes.element,
}

//////////////////////////////////////////// OUTPUT METHOD HELP
export function DialogOutputMethodHelp(props) {

  const {t} = useTranslation();
  const {toggleDialog, open} = props;
  const outputMethods = React.useMemo(()=>{
    return [
      {name: t("requestForm.modifyFile.methods.descriptiveMethod"), items: t("requestForm.modifyFile.methods.descriptiveMethodExamples", { returnObjects: true })},
      {name: t("requestForm.modifyFile.methods.scalingMethod"), items: t("requestForm.modifyFile.methods.scalingMethodExamples", { returnObjects: true })},
      {name: t("requestForm.modifyFile.methods.graphsMethod"), items: t("requestForm.modifyFile.methods.graphsMethodExamples", { returnObjects: true })},
      {name: t("requestForm.modifyFile.methods.regressionMethod"), items: t("requestForm.modifyFile.methods.regressionMethodExamples", { returnObjects: true })},
      {name: t("requestForm.modifyFile.methods.modelingMethod"), items: t("requestForm.modifyFile.methods.modelingMethodExamples", { returnObjects: true })}
    ];
  },[t]);

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <StyledDialog
      onClose={(e) => {
        toggleDialog(e);
      }}
      aria-labelledby="dialog-output-method-help"
      open={open}
      className={classes.root}
      scroll="paper"
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dialog-output-method-help" component="div">
        <div className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {t('dialog.outputMethodHelpTitle')}
          </Typography>
          <IconButton
            id="dialog-close"
            onClick={toggleDialog}
            edge="end"
            aria-label={t('dialog.close')}
            onKeyPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === 'Enter') {
                toggleDialog(e);
              }
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          {outputMethods.map((method, i)=>{
            let content = ""
            method.items.forEach(item=>{
              content+=item+", "
            });
            content=content.slice(0,-2);
            return (
              <React.Fragment key={i}>
                <div className={classes.vettingRow}>
                  <div className={classes.vettingColumn}>
                    <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
                      {method.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                    >
                      {t("requestForm.modifyFile.examplesInclude", {content:content})}
                    </Typography>
                  </div>
                </div>
                {i<outputMethods.length-1 &&
                  <Divider className={classes.divider} />
                }
              </React.Fragment>
            )
          })}
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          id="outputMethodHelp-close-btn"
          type="submit"
          variant="contained"
          color="primary"
          className={classes.footerBtns}
          onClick={(e) => {
            toggleDialog(e);
          }}
          onKeyPress={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
        >
          {t('dialog.outputMethodHelpCloseButton')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
DialogOutputMethodHelp.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}

//////////////////////////////////////////// ADD FILE
export function DialogFileSupport(props) {

  const {t} = useTranslation();
  const {
    submitDialog, 
    toggleDialog, 
    handleOnChange,
    open, 
    filesList, 
    newSupporting, 
    index,
    fileType
  } = props;
  const [fileError, setFileError] = React.useState("");

  const handleTitle=()=>{
    return index !== null ?
      t('dialog.addFileTitleEdit') :
      t('dialog.addFileTitleAdd');
  }

  const handlePrimaryButton=()=>{
    return index !== null ?
      t('dialog.addFileUpdate') :
      t('dialog.addFileAdd');   
  }

  const onSubmit=()=>{
    if(newSupporting.fileName===""){
      setFileError("dialog.addFileError");
    }else{
      setFileError("");
      submitDialog();
    }
  }

  const onClose=()=>{
    setFileError("");
    toggleDialog();
  }

  const content=()=>(
    <>
      <Typography variant="subtitle2" component="h3" sx={{mb: 3}}>
        {t("dialog.addFileContent",{context: fileType})}
      </Typography>
      <Autocomplete
        sx={{m:0, mb: 3}}
        componentsProps={{
          clearIndicator: {tabIndex: 0},
        }}
        id="dialogAddFile-fileName"
        options={filesList}
        renderInput={(params) => (
          <TextField
            {...params}
            multiline
            minRows="1"
            label={t("dialog.addFileFilePath")}
            required={true}
            variant="outlined"
            error={Boolean(t(fileError))}
            helperText={t(fileError)}
          />
        )}
        value={newSupporting.fileName==="" ? null : newSupporting.fileName}
        onChange={(event, newValue) => {
          if(newValue===null){
            newValue="";
          }
          if(newValue!==""){
            setFileError("");
          }
          handleOnChange("fileName", newValue);
        }}
      />
      <CutCopyPasteTextField
        id="dialogAddFile-notes"
        className={classes.inputMargin}
        margin="dense"
        name="notes"
        label={t('dialog.addFileNotes')}
        variant="outlined"
        fullWidth
        multiline
        minRows="3"
        onBlur={(event) => {
          handleOnChange("notes", event.target.value);
        }}
        defaultValue={newSupporting.notes}
      />
      {props.snackbar ? props.snackbar : null}
    </>
  );

  return (
    <>
      <CustomDialog
        id="file-support-dialog"
        open={open}
        title={handleTitle()}
        content={content()}
        primaryButton={handlePrimaryButton()}
        secondaryButton={t('dialog.addFileCancel')}
        handlePrimaryClick={() => onSubmit()}
        handleSecondaryClick={() => onClose()}
        toggleDialog={() => onClose()}
      />
      {props.snackbar ? props.snackbar : null}
    </>
  );
}
DialogFileSupport.propTypes={
  submitDialog: PropTypes.func.isRequired, 
  toggleDialog: PropTypes.func.isRequired, 
  handleOnChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired, 
  filesList: PropTypes.arrayOf(PropTypes.string).isRequired, 
  newSupporting: PropTypes.object.isRequired, 
  index: PropTypes.number,
  fileType: PropTypes.string.isRequired,
  snackbar: PropTypes.element,
}